import React, { useMemo } from 'react';
import { ArrowDown, ArrowUp, Minus, type LucideIcon } from 'lucide-react';
import { fmtDelta } from '../../utils/format';

interface KpiCardProps {
  label: string;
  /** Pre-formatted value (e.g. "$1,234"). Number → formatted automatically. */
  value: React.ReactNode;
  /** Optional secondary line. */
  sub?: React.ReactNode;
  /** Compare current to previous — adds an up/down chip below the value. */
  current?: number;
  previous?: number;
  /** Flip semantic: lower-is-better (bounce rate, errors). */
  invertDelta?: boolean;
  /** Optional sparkline series — last ~30 data points. */
  spark?: number[];
  /** Icon shown in upper-right. */
  Icon?: LucideIcon;
  /** Accent border + value tint — use for the headline KPI. */
  accent?: boolean;
}

export const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  sub,
  current,
  previous,
  invertDelta,
  spark,
  Icon,
  accent = false,
}) => {
  const delta = current != null && previous != null
    ? fmtDelta(current, previous, { invert: invertDelta })
    : null;

  return (
    <div className={`hf-kpi ${accent ? 'accent' : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span className="hf-kpi-label">{label}</span>
        {Icon && (
          <span style={{ color: 'var(--fg-3)' }} aria-hidden="true">
            <Icon size={13} />
          </span>
        )}
      </div>
      <div className="hf-kpi-row">
        <span className="hf-kpi-value">{value}</span>
        {spark && spark.length >= 2 && (
          <Sparkline points={spark} accent={accent} positive={delta?.trend !== 'down'} />
        )}
      </div>
      {delta && delta.label !== '—' && (
        <span
          className={`hf-kpi-delta ${delta.trend === 'up' ? 'up' : delta.trend === 'down' ? 'down' : ''}`}
        >
          {delta.trend === 'up'   && <ArrowUp size={11} />}
          {delta.trend === 'down' && <ArrowDown size={11} />}
          {delta.trend === 'flat' && <Minus size={11} />}
          <span className="hf-mono">{delta.label}</span>
          <span style={{ color: 'var(--fg-3)' }}>vs prev</span>
        </span>
      )}
      {sub && <span className="hf-kpi-sub">{sub}</span>}
    </div>
  );
};

const Sparkline: React.FC<{ points: number[]; accent?: boolean; positive?: boolean }> = ({
  points,
  positive = true,
}) => {
  const { d, area, h, w, last, max, min } = useMemo(() => {
    const w = 72;
    const h = 22;
    const max = Math.max(...points);
    const min = Math.min(...points);
    const range = max - min || 1;
    const stepX = points.length > 1 ? w / (points.length - 1) : 0;
    const project = (n: number, i: number) => [
      i * stepX,
      h - ((n - min) / range) * (h - 2) - 1
    ] as const;
    const pts = points.map(project);
    const d = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${d} L${w},${h} L0,${h} Z`;
    return { d, area, h, w, last: pts[pts.length - 1], max, min };
  }, [points]);

  const stroke = positive ? 'var(--success)' : 'var(--danger)';
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
      style={{ overflow: 'visible' }}
    >
      <path d={area} fill={stroke} opacity={0.12} />
      <path d={d} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      {last && <circle cx={last[0]} cy={last[1]} r={1.8} fill={stroke} />}
      <title>{`min ${min.toFixed(0)} · max ${max.toFixed(0)}`}</title>
    </svg>
  );
};
